import { IBasicUser } from '@faire/web-api--source/indigofair/data/IBasicUser';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IMessengerConversationParticipant } from '@faire/web-api--source/indigofair/data/IMessengerConversationParticipant';
import { IMessengerMessage } from '@faire/web-api--source/indigofair/data/IMessengerMessage';
import { MessengerConversationType } from '@faire/web-api--source/indigofair/data/MessengerConversationType';

export namespace IMessengerConversation {
  /**
   * Return an instance of IMessengerConversation with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IMessengerConversation>
  ): IMessengerConversation =>
    ({
      participants: [],
      ...(partial ?? {}),
    }) as IMessengerConversation;
}

export interface IMessengerConversation {
  /**
   * The token for this conversation.
   */
  token: string | undefined;
  /**
   * The type of this conversation.
   */
  type: keyof typeof MessengerConversationType | undefined;
  /**
   * The other party in this conversation.
   */
  other_party: IBasicUser | undefined;
  /**
   * This was only used for support conversations and has been deprecated
   * @deprecated
   */
  other_party_status: string | undefined;
  /**
   * The total number of messages in this conversation.
   */
  total_messages: number | undefined;
  /**
   * The number of unread messages by the logged in user in this conversation.
   */
  unread_messages: number | undefined;
  /**
   * When this conversation was created.
   */
  created_at: number | undefined;
  /**
   * When this conversation was last updated.
   */
  updated_at: number | undefined;
  /**
   * When the logged in user last read messages in this conversation.
   */
  last_read_at: number | undefined;
  /**
   * The most recent message sent to this conversation.
   */
  latest_message: IMessengerMessage | undefined;
  /**
   * When the most recent email notification for the conversation was sent.
   */
  last_email_notification_sent_at: number | undefined;
  /**
   * Image for other party at the user level. Used for message-level spaces
   */
  other_party_image: IImage | undefined;
  /**
   * Image for other party at the business level. Used for conversation-level spaces
   */
  other_party_business_image: IImage | undefined;
  /**
   * Description of other party.
   */
  other_party_description: string | undefined;
  /**
   * Company of other party*
   */
  other_party_company: string | undefined;
  /**
   * Display Name of other party
   */
  other_party_name: string | undefined;
  /**
   * Is the other party currently accepting messages. I.e. support hours for Faire, or not on vacation for brands.
   */
  is_accepting_messages: boolean | undefined;
  /**
   * Is the conversation starred by brand
   */
  is_starred_by_brand: boolean | undefined;
  /**
   * Is the conversation marked as spam
   */
  marked_as_spam: boolean | undefined;
  /**
   * List of participants in the conversation sorted by
   */
  participants: Array<IMessengerConversationParticipant>;
  /**
   * @protobufOption (.faire.common.tokenType) = BRAND
   */
  brand_token: string | undefined;
  /**
   * @protobufOption (.faire.common.tokenType) = RETAILER
   */
  retailer_token: string | undefined;
}
